import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { JURISDICTION_GROUPS } from '../../utils/jurisdictionData';
import {
    ChevronLeft,
    TrendingUp,
    Award,
    Activity,
    Hexagon,
    Flame,
    Building2,
} from 'lucide-react';

/* ─────────────────────────────────────────────
 *  COUNTY COLOR PALETTE (light theme)
 * ───────────────────────────────────────────── */
const COUNTY_COLORS = {
    'blue-400': { accent: '#3b82f6', mid: '#60a5fa', soft: 'rgba(59,130,246,0.09)', ring: 'rgba(59,130,246,0.25)', text: '#1d4ed8' },
    'indigo-400': { accent: '#818cf8', mid: '#c7d2fe', soft: 'rgba(129,140,248,0.12)', ring: 'rgba(129,140,248,0.35)', text: '#4338ca' },
    'emerald-400': { accent: '#10b981', mid: '#34d399', soft: 'rgba(16,185,129,0.09)', ring: 'rgba(16,185,129,0.25)', text: '#065f46' },
    'amber-400': { accent: '#f59e0b', mid: '#fbbf24', soft: 'rgba(245,158,11,0.09)', ring: 'rgba(245,158,11,0.25)', text: '#92400e' },
    'rose-400': { accent: '#fb7185', mid: '#fda4af', soft: 'rgba(251,113,133,0.12)', ring: 'rgba(251,113,133,0.35)', text: '#be123c' },
};
const getColors = (cls) => COUNTY_COLORS[cls] || COUNTY_COLORS['blue-400'];

/* ═══════════════════════════════════════════════
 *  JURISDICTION ACCENT — Professional Vertical Indicator
 * ═══════════════════════════════════════════════ */
const JurisdictionAccent = ({ pct, colors, rank }) => {
    // Top rank (Leader) gets a more prominent accent height
    const accentHeight = rank === 1 ? Math.max(pct, 25) : pct;

    return (
        <div className="absolute left-0 top-0 bottom-0 w-1 overflow-hidden rounded-l-xl z-20">
            <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: `${accentHeight}%`, opacity: 1 }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                className="absolute bottom-0 left-0 right-0"
                style={{
                    background: `linear-gradient(to top, ${colors.accent}, ${colors.mid})`,
                    boxShadow: `1px 0 10px ${colors.ring}`
                }}
            >
                {/* Subtle top-edge shine */}
                <div className="absolute top-0 left-0 right-0 h-px bg-white/40" />
            </motion.div>
        </div>
    );
};

/* ═══════════════════════════════════════════════
 *  GLASS GLINT — Subtle Iridescent Border
 * ═══════════════════════════════════════════════ */
const GlassGlint = ({ isHovered, colors }) => (
    <motion.div
        className="absolute inset-0 rounded-[inherit] pointer-events-none"
        animate={{
            opacity: isHovered ? 1 : 0,
            background: [
                `linear-gradient(45deg, transparent 0%, ${colors.soft} 50%, transparent 100%)`,
                `linear-gradient(135deg, transparent 0%, ${colors.soft} 50%, transparent 100%)`,
                `linear-gradient(225deg, transparent 0%, ${colors.soft} 50%, transparent 100%)`,
                `linear-gradient(315deg, transparent 0%, ${colors.soft} 50%, transparent 100%)`,
            ]
        }}
        transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
        style={{ padding: 1.5 }}
    >
        <div className="w-full h-full rounded-[inherit] bg-transparent" />
    </motion.div>
);

/* ═══════════════════════════════════════════════
 *  KPI CHIP
 * ═══════════════════════════════════════════════ */
const KpiChip = ({ icon, value, label, colors, isText = false }) => (
    <motion.div
        whileHover={{ y: -2, scale: 1.03 }}
        className="flex items-center gap-2 sm:gap-2.5 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl sm:rounded-2xl flex-shrink-0"
        style={{
            background: 'rgba(255, 255, 255, 0.4)',
            border: `1px solid ${colors.ring}`,
            backdropFilter: 'blur(8px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
        }}
    >
        <span style={{ color: colors.accent }}>{icon}</span>
        <div>
            <p
                className={`font-extrabold leading-none tabular-nums ${isText ? 'text-[10px] sm:text-xs' : 'text-sm sm:text-base'}`}
                style={{ color: colors.text }}
            >
                {value}
            </p>
            <p className="text-[7px] sm:text-[8px] font-bold uppercase tracking-widest text-slate-400 mt-0.5">{label}</p>
        </div>
    </motion.div>
);

/* ═══════════════════════════════════════════════
 *  MOSAIC TILE — Bento component
 * ═══════════════════════════════════════════════ */
const JurisdictionMosaicTile = ({ city, rank, pct, share, isHero, colors, average, index, isHovered, onHover }) => {
    const isTopThree = rank <= 3;

    /* 3D TILT LOGIC */
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [15, -15]), { stiffness: 300, damping: 30 });
    const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-15, 15]), { stiffness: 300, damping: 30 });

    const handleMouseMove = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const width = rect.width;
        const height = rect.height;
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;
        x.set(mouseX / width - 0.5);
        y.set(mouseY / height - 0.5);
    };

    const handleMouseLeave = () => {
        x.set(0);
        y.set(0);
        onHover(null);
    };

    // Formatting name for compact tiles
    const displayName = city.name.length > 12 && !isHero ? city.name.substring(0, 10) + '..' : city.name;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.9, filter: 'blur(10px)' }}
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, scale: 0.9, filter: 'blur(10px)' }}
            transition={{
                delay: index * 0.03,
                duration: 0.5,
                ease: [0.23, 1, 0.32, 1]
            }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onHoverStart={() => onHover(city.name)}
            className={`group relative h-full min-h-[110px] sm:min-h-[140px] ${isHero ? 'col-span-2' : 'col-span-1'}`}
            style={{ perspective: 1000 }}
        >
            <motion.div
                style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
                className="h-full w-full"
            >
                <div
                    className="h-full rounded-2xl p-3 sm:p-5 flex flex-col items-center justify-between transition-all duration-500 overflow-hidden relative"
                    style={{
                        background: isHovered ? 'white' : 'rgba(255, 255, 255, 0.6)',
                        border: isHovered ? `1px solid ${colors.accent}` : '1px solid #e2e8f0',
                        boxShadow: isHovered ? `0 25px 50px -12px ${colors.ring}` : '0 4px 12px rgba(0,0,0,0.02)',
                        backdropFilter: 'blur(20px)',
                    }}
                >
                    {/* Professional Vertical Accent */}
                    <JurisdictionAccent pct={pct} colors={colors} rank={rank} />

                    {/* Iridescent Border Glint */}
                    <GlassGlint isHovered={isHovered} colors={colors} />

                    {/* TOP-RIGHT: RANK BADGE */}
                    <div className="absolute top-3 right-3 z-10">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black shadow-lg"
                            style={{
                                background: isTopThree ? `linear-gradient(135deg, ${colors.accent}, ${colors.mid})` : `linear-gradient(135deg, ${colors.accent}, ${colors.mid})`,
                                color: isTopThree ? 'white' : 'white',
                                border: isTopThree ? 'none' : `1px solid ${colors.ring}`
                            }}
                        >
                            {rank}
                        </motion.div>
                    </div>

                    {/* TOP-LEFT: HERO INDICATOR */}
                    {isHero && (
                        <div className="absolute top-4 left-4 z-10 flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-emerald-500 ring-4 ring-emerald-100 shadow-[0_0_12px_rgba(16,185,129,0.5)]" />
                            <span className="text-[7px] font-black text-emerald-600 uppercase tracking-widest bg-emerald-50/50 px-1.5 py-0.5 rounded-md border border-emerald-100/50">
                                Top Market
                            </span>
                        </div>
                    )}

                    {/* MID: CITY NAME (Focal Point) */}
                    <motion.div
                        initial={{ y: 10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: index * 0.05 + 0.1 }}
                        className="mt-6 sm:mt-8 w-full text-center relative z-10 px-2"
                    >
                        <h3
                            className={`font-black tracking-tight leading-none transition-all duration-300 ${isHero ? 'text-lg sm:text-xl' : 'text-xs sm:text-sm'}`}
                            style={{
                                color: isHovered ? colors.text : '#0f172a',
                                letterSpacing: '-0.02em'
                            }}
                            title={city.name}
                        >
                            {displayName}
                        </h3>
                        {isHero && (
                            <div className="mt-2 text-[8px] font-bold text-slate-400 uppercase tracking-[0.3em] overflow-hidden">
                                <motion.span
                                    animate={{ opacity: [0.4, 1, 0.4] }}
                                    transition={{ duration: 2, repeat: Infinity }}
                                >
                                    Analytical Drilled Center
                                </motion.span>
                            </div>
                        )}
                    </motion.div>

                    {/* BOTTOM: ANALYTICAL ROW */}
                    <motion.div
                        initial={{ y: 10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: index * 0.05 + 0.2 }}
                        className="w-full flex items-end justify-between relative z-10 mt-auto"
                    >
                        <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1.5 text-slate-400">
                                <Building2 className="w-2.5 h-2.5" />
                                <span className="text-[7px] sm:text-[8px] font-black uppercase tracking-widest leading-none">Units</span>
                            </div>
                            <span
                                className={`font-black tabular-nums leading-none ${isHero ? 'text-2xl sm:text-4xl' : 'text-sm sm:text-base'}`}
                                style={{
                                    color: isHovered ? colors.text : colors.accent,
                                    textShadow: isHero && isHovered ? `0 0 20px ${colors.ring}` : 'none'
                                }}
                            >
                                {city.count}
                            </span>
                        </div>

                        <div className="flex flex-col items-end gap-1.5">
                            <div className="px-2 py-1 rounded-lg bg-slate-50 border border-slate-100 flex items-center gap-1 shadow-sm">
                                <span className="text-[8px] sm:text-[9px] font-black tabular-nums" style={{ color: colors.text }}>
                                    {share}%
                                </span>
                                <span className="text-[6px] font-bold text-slate-400 uppercase tracking-tighter">Share</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </motion.div>
        </motion.div>
    );
};

/* ═══════════════════════════════════════════════
 *  MAIN COMPONENT
 * ═══════════════════════════════════════════════ */
const CityAnalysisSection = ({ selectedCounty, selectedCity, analysisHistory, onBack, externalCityCounts }) => {
    const countyData = useMemo(() => {
        if (selectedCounty === 'Other') {
            return {
                county: 'Other',
                color: 'bg-slate-400',
                cities: []
            };
        }
        return JURISDICTION_GROUPS.find(g => g.county === selectedCounty);
    }, [selectedCounty]);

    const [hoveredCity, setHoveredCity] = useState(null);

    const cityCounts = useMemo(() => {
        if (externalCityCounts) return externalCityCounts;
        if (!analysisHistory || !countyData) return {};
        const counts = {};
        analysisHistory.forEach(item => {
            const addr = (item.address || '').toLowerCase();
            const found = countyData.cities.find(c => addr.includes(c.toLowerCase()));
            if (found) counts[found] = (counts[found] || 0) + 1;
        });
        return counts;
    }, [analysisHistory, countyData, externalCityCounts]);

    const stats = useMemo(() => {
        const total = Object.values(cityCounts).reduce((s, c) => s + c, 0);
        const sorted = Object.entries(cityCounts).sort((a, b) => b[1] - a[1]);
        const top = sorted[0];

        let cityArray = [];
        if (selectedCounty === 'Other') {
            cityArray = Object.entries(cityCounts).map(([name, count]) => ({ name, count }));
        } else if (countyData) {
            cityArray = countyData.cities.map(c => ({ name: c, count: cityCounts[c] || 0 }));
        }

        return {
            total,
            topCity: top ? top[0] : '—',
            topCount: top ? top[1] : 0,
            average: (total / (cityArray.length || 1)).toFixed(1),
            cityArray: cityArray.sort((a, b) => b.count - a.count)
        };
    }, [cityCounts, countyData, selectedCounty]);

    if (!selectedCounty || (!countyData && selectedCounty !== 'Other')) return null;

    const colorClass = (countyData?.color || 'bg-slate-400').replace('bg-', '');
    const colors = getColors(colorClass);
    const allCities = stats.cityArray;
    const activeTotal = allCities.reduce((s, c) => s + c.count, 0);

    return (
        <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -18 }}
            transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
        >
            {/* ─── OUTER CARD ─── */}
            <div
                className="rounded-xl overflow-hidden"
                style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 6px 32px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)',
                }}
            >
                {/* Coloured top stripe */}
                <div style={{
                    height: 5,
                    background: `linear-gradient(90deg, ${colors.accent} 0%, ${colors.mid} 100%)`,
                }} />

                <div className="p-4">

                    {/* ═══ HEADER ═══ */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-5 mb-3">
                        <div className="flex items-center gap-3">
                            <motion.button
                                whileHover={{ scale: 1.08, backgroundColor: '#f1f5f9' }}
                                whileTap={{ scale: 0.92 }}
                                onClick={onBack}
                                className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                                style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}
                            >
                                <ChevronLeft className="w-4 h-4 text-slate-500" />
                            </motion.button>
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <Hexagon className="w-3 h-3 flex-shrink-0" style={{ color: colors.accent }} />
                                    <span
                                        className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.22em] truncate"
                                        style={{ color: colors.accent }}
                                    >
                                        {selectedCounty} {selectedCounty === 'Other' ? 'Region' : 'County'}
                                    </span>
                                </div>
                                <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-slate-900 tracking-tight">
                                    Jurisdiction{' '}
                                    <span style={{ color: colors.accent }}>Insights</span>
                                </h1>
                            </div>
                        </div>

                        {/* KPI Chips — horizontal scroll on mobile */}
                        <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
                            <KpiChip icon={<Activity className="w-3 sm:w-3.5 h-3 sm:h-3.5" />} value={stats.total} label="Properties" colors={colors} />
                            <KpiChip icon={<Award className="w-3 sm:w-3.5 h-3 sm:h-3.5" />} value={stats.topCity} label="Leader" colors={colors} isText />
                            <KpiChip icon={<TrendingUp className="w-3 sm:w-3.5 h-3 sm:h-3.5" />} value={stats.average} label="Avg/City" colors={colors} />
                        </div>
                    </div>

                    {/* ═══ COMPACT JURISDICTION MOSAIC (Bento Grid) ═══ */}
                    <div className="relative mt-4 p-1 sm:p-2 rounded-[2.5rem] overflow-hidden">
                        {/* ─── ATMOSPHERIC MESH BACKDROP ─── */}
                        <div className="absolute inset-0 pointer-events-none opacity-20">
                            <motion.div
                                animate={{
                                    scale: [1, 1.2, 1],
                                    rotate: [0, 90, 180, 270, 360],
                                    opacity: [0.3, 0.5, 0.3]
                                }}
                                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                                className="absolute -top-1/4 -left-1/4 w-[150%] h-[150%] blur-[120px] opacity-20"
                                style={{ background: `conic-gradient(from 0deg, ${colors.accent}, ${colors.mid}, transparent, ${colors.accent})` }}
                            />
                            {/* Floating Micro-Particles */}
                            {[...Array(12)].map((_, i) => (
                                <motion.div
                                    key={i}
                                    className="absolute w-1 h-1 rounded-full bg-slate-300/30"
                                    initial={{ x: Math.random() * 1000, y: Math.random() * 500 }}
                                    animate={{
                                        y: [0, -100],
                                        opacity: [0, 1, 0],
                                        scale: [0, 1.5, 0]
                                    }}
                                    transition={{
                                        duration: 5 + Math.random() * 5,
                                        repeat: Infinity,
                                        delay: Math.random() * 5
                                    }}
                                />
                            ))}
                        </div>

                        <div className="flex items-center justify-between mb-6 relative z-10 px-2 sm:px-4">
                            <div className="flex items-center gap-3">
                                <div
                                    className="p-2 rounded-xl border border-slate-200 transition-all group hover:scale-110"
                                    style={{ background: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}
                                >
                                    <Hexagon className="w-4 h-4" style={{ color: colors.accent }} />
                                </div>
                                <div className="space-y-0.5">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.25em] block">
                                        Jurisdiction Mosaic
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-sm sm:text-base font-black text-slate-900 tracking-tight">Market Concentration</h2>
                                        <div className="px-1.5 py-0.5 rounded-md bg-emerald-50 border border-emerald-100 flex items-center gap-1">
                                            <div className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" />
                                            <span className="text-[8px] font-black text-emerald-600 uppercase tracking-widest">Dynamic</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Bento Grid Matrix */}
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 relative z-10">
                            <AnimatePresence mode="popLayout">
                                {allCities.length === 0 ? (
                                    <motion.div
                                        layout
                                        className="col-span-full py-16 text-center rounded-3xl border border-dashed border-slate-200"
                                    >
                                        <Activity className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                                        <p className="text-sm font-bold text-slate-400">No concentration data yet.</p>
                                    </motion.div>
                                ) : (
                                    allCities.map((city, idx) => {
                                        const pct = stats.topCount > 0 ? (city.count / stats.topCount) * 100 : 0;
                                        const share = activeTotal > 0 ? ((city.count / activeTotal) * 100).toFixed(1) : '0.0';
                                        // Top 3 jurisdictions get "Hero Cards" (larger footprint)
                                        const isHero = idx < 3 && city.count > 0;

                                        return (
                                            <JurisdictionMosaicTile
                                                key={city.name}
                                                city={city}
                                                rank={idx + 1}
                                                pct={pct}
                                                share={share}
                                                isHero={isHero}
                                                colors={colors}
                                                average={stats.average}
                                                index={idx}
                                                isHovered={hoveredCity === city.name || (selectedCity !== "All" && selectedCity === city.name)}
                                                onHover={setHoveredCity}
                                            />
                                        );
                                    })
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Mini Stats Bar */}
                        {allCities.length > 0 && (
                            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 px-2">
                                <div className="flex items-center gap-6">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                                            {allCities.filter(c => c.count > 0).length} Jurisdictions Analyzed
                                        </span>
                                    </div>
                                    <div className="h-4 w-px bg-slate-200" />
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Avg Output:</span>
                                        <span className="text-[11px] font-black tabular-nums" style={{ color: colors.text }}>
                                            {stats.average} Units
                                        </span>
                                    </div>
                                </div>
                                <div
                                    className="flex items-center gap-2 px-3 py-1 rounded-lg border border-slate-200/60 shadow-sm"
                                    style={{ background: 'white' }}
                                >
                                    <Flame className="w-3 h-3 animate-pulse" style={{ color: colors.accent }} />
                                    <span className="text-[11px] font-black tabular-nums" style={{ color: colors.text }}>
                                        {activeTotal} Total Scope
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default CityAnalysisSection;
